import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Play, Pause, ChevronUp, Users, Radio as RadioIcon, Volume2 } from "lucide-react";
import { useRadio } from "@/context/RadioContext";
import { Visualizer } from "./Visualizer";
import { FlameButton } from "./FlameButton";
import { ReactionChips } from "./ReactionChips";
import { Cover } from "./Bits";
import { fmtTime } from "@/lib/api";

export const PlayButton = ({ size = 48, testid = "play-pause-button" }) => {
  const { playing, toggle } = useRadio();
  return (
    <button data-testid={testid} onClick={toggle} aria-label={playing ? "Pause" : "Play"}
      style={{ width: size, height: size }}
      className="flex shrink-0 items-center justify-center rounded-full bg-[#FF2E63] text-white transition-[transform,box-shadow] duration-200 hover:scale-105 hover:shadow-[0_0_30px_rgba(255,46,99,.6)] active:scale-95">
      {playing ? <Pause className="h-1/2 w-1/2" fill="currentColor" /> : <Play className="ml-0.5 h-1/2 w-1/2" fill="currentColor" />}
    </button>
  );
};

export const NowTitle = ({ cur }) => (
  <div className="min-w-0">
    <div className="flex items-center gap-2">
      <span className="live-dot" />
      <span className="font-mono text-[10px] uppercase tracking-[.25em] text-[#FF2E63]">{cur?.hidden ? "Blind Drop" : cur?.premiere ? "Premiere" : "On air"}</span>
    </div>
    <div data-testid="player-track-title" className="truncate font-display text-sm font-bold sm:text-base">{cur ? cur.title : "Tuning in…"}</div>
    <div data-testid="player-artist-name" className="truncate text-xs text-slate-400">
      {cur?.hidden ? "Flame to reveal" : cur?.artist ? <Link className="hover:text-[#00F5D4]" to={`/a/${cur.artist.slug}?play=${cur.play_id}`}>{cur.artist.stage_name}</Link> : ""}
    </div>
  </div>
);

const Expanded = ({ state }) => (
  <div className="grid gap-6 border-b border-white/5 px-4 py-5 md:grid-cols-3 md:px-8" data-testid="player-expanded">
    <div>
      <div className="label-mono mb-3">What hits you?</div>
      <ReactionChips />
    </div>
    <div>
      <div className="label-mono mb-3">Up next</div>
      {state?.upcoming?.map((u) => (
        <div key={u.play_id} className="flex justify-between py-1 text-sm"><span className="truncate text-slate-300">{u.hidden ? "Blind Drop" : `${u.title} — ${u.artist?.stage_name}`}</span><span className="font-mono text-xs text-slate-500">{fmtTime(u.start)}</span></div>
      ))}
    </div>
    <div>
      <div className="label-mono mb-3">Just played</div>
      {state?.history?.slice(0, 3).map((h) => (
        <div key={h.play_id} className="flex justify-between py-1 text-sm"><span className="truncate text-slate-300">{h.title} — {h.artist?.stage_name}</span><span className="font-mono text-xs text-slate-500">{fmtTime(h.start)}</span></div>
      ))}
    </div>
  </div>
);

export const RadioPlayer = () => {
  const { state, playing, volume, setVolume } = useRadio();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const cur = state?.current;
  const lift = pathname.startsWith("/join") ? "bottom-[76px] md:bottom-0" : "bottom-0";
  return (
    <div data-testid="persistent-radio-player" className={`fixed left-0 right-0 z-50 ${lift} border-t border-white/10 bg-[#07090E]/92 backdrop-blur-2xl`}>
      {open && <Expanded state={state} />}
      <div className="flex items-center gap-3 px-3 py-2.5 md:gap-5 md:px-8">
        <Cover src={cur?.cover} hidden={cur?.hidden} className="h-11 w-11 shrink-0 rounded-lg md:h-12 md:w-12" testid="player-cover" />
        <PlayButton size={44} />
        <div className="min-w-0 flex-1 md:max-w-xs"><NowTitle cur={cur} /></div>
        <div className="hidden h-10 flex-1 md:block"><Visualizer active={playing} bars={64} className="h-10" testid="player-visualizer" /></div>
        <div className="hidden items-center gap-1.5 font-mono text-xs text-slate-400 lg:flex" data-testid="player-listeners"><Users className="h-3.5 w-3.5 text-[#00F5D4]" />{state?.listeners ?? 0}</div>
        <div className="hidden items-center gap-2 lg:flex"><Volume2 className="h-4 w-4 text-slate-400" />
          <input data-testid="volume-slider" type="range" min="0" max="1" step="0.05" value={volume} onChange={(e) => setVolume(+e.target.value)} className="w-20 accent-[#00F5D4]" />
        </div>
        <FlameButton />
        <Link to="/radio" data-testid="player-radio-link" className="hidden text-slate-400 hover:text-[#00F5D4] sm:block"><RadioIcon className="h-5 w-5" /></Link>
        <button data-testid="player-expand-button" onClick={() => setOpen(!open)} className="-m-2 p-3 text-slate-400 transition-transform hover:text-white" style={{ transform: open ? "rotate(180deg)" : "none" }}>
          <ChevronUp className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};
